/**
 * Class to attach to the body element when the movie-site component is used.
 * This class is used to apply styles that override the styles of the surrounding components.
 */
export const BODY_CLASS_NAME = 'movie-branded-page-site';

/**
 * Expected CSS variables for the movie site theme to use outside the component
 */
type MOVIE_SITE_OUT_VARIABLES =
  | '--movie-site_brand-color'
  | '--movie-site_background-color'
  | '--movie-site_brand-color-contrast'
  | '--movie-site_brand-color-hover'
  | '--movie-site_brand-color-shaded'
  | '--movie-site_brand-color-support'
  | '--movie-site_brand-color-support-contrast'
  | '--movie-site_background-color-contrast'
  | '--movie-site_background-color-shaded'
  | '--movie-site_background-color-support'
  | '--movie-site_background-color-support-contrast'
  | '--movie-site_footer-subscribe-form-background-color'
  | '--movie-site_footer-subscribe-form-background-color-contrast'
  | '--movie-site_border-radius';

function getVar(name: MOVIE_SITE_OUT_VARIABLES): string {
  return `var(${name})`;
}

/**
 * !NOTE: This should be inserted in the resolver of the movie-site component?
 * Right now this is inserted inside the movie-site-service.ts.
 */

/**
 * CSS that overrides the styles OUTSIDE the movie-site component.
 * This is used to ensure that the movie-site component can be used in any context
 * without being affected by the styles of the surrounding components.
 *
 * RECOMMEND use the BODY_CLASS_NAME to don't affect outside the movie-site.
 */
export const MOVIE_SITE_OVERRIDE_CSS = `
      .${BODY_CLASS_NAME} {
        --progress-bar-color: ${getVar('--movie-site_brand-color')};
      }

      .${BODY_CLASS_NAME} ng-progress :is(.ng-spinner, .ng-meteor) {
        display: none !important;
      }

      .${BODY_CLASS_NAME} .page-wrapper {
        overflow: visible;
      }
      .${BODY_CLASS_NAME} ush-header {
        display: none !important;
      }

      .${BODY_CLASS_NAME} app-go-to-top {
        
      }

      .${BODY_CLASS_NAME} .page-wrapper > #main-footer {
        display: none !important;
      }

      .${BODY_CLASS_NAME} .footer-component {
        padding-top: 40px !important;
      } 

      .${BODY_CLASS_NAME} :is(#footer-container, .footer-wrapper) {
        background-color: ${getVar('--movie-site_background-color')} !important;
      }

      .${BODY_CLASS_NAME} .footer-wrapper :is(.border) {
        background-color: ${getVar('--movie-site_brand-color')} !important;
      }

      .${BODY_CLASS_NAME} .footer-wrapper :is(:hover, :focus) :is(.border) {
        background-color: ${getVar('--movie-site_brand-color')} !important;
      }

      .${BODY_CLASS_NAME} .footer-wrapper :is(p, span, a, fa-icon):not(.isolated-from-movie-site, .isolated-from-movie-site *) {
        color: ${getVar('--movie-site_background-color-contrast')} !important;
      }

      .${BODY_CLASS_NAME} .footer-wrapper :is(a) {
        text-decoration-color: ${getVar('--movie-site_brand-color')} !important;
      }

      .${BODY_CLASS_NAME} ush-footer {
        --subscribeResultMsgColor: ${getVar('--movie-site_background-color-contrast')};
        position: relative;
        background-color: ${getVar('--movie-site_background-color')};
      }


      .${BODY_CLASS_NAME} #footer-subscribe-form .subscribe-form {
        background-color: var(--movie-site_footer-subscribe-form-background-color, ${getVar('--movie-site_brand-color-support')}) !important;
        color: var(--movie-site_footer-subscribe-form-background-color-contrast, ${getVar('--movie-site_brand-color-support-contrast')}) !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
      }

      .${BODY_CLASS_NAME} #footer-subscribe-form :is(.subscribe-title, .subscribe-desc, .result-msg, .text-danger) {
        color: var(--movie-site_footer-subscribe-form-background-color-contrast, ${getVar('--movie-site_brand-color-support-contrast')}) !important;
      }

      .${BODY_CLASS_NAME} #footer-subscribe-form .subscribe-form  .input-wrapper {
        width: 100% !important;
      }

      .${BODY_CLASS_NAME} #footer-subscribe-form .subscribe-form  input {
        padding: var(--subscribeInputPadding, var(--_subscribe-input-padding)) !important;
        background-color: var(--movie-site_footer-subscribe-form-background-color, ${getVar('--movie-site_brand-color-support')}) !important;
        color: var(--movie-site_footer-subscribe-form-background-color-contrast, ${getVar('--movie-site_brand-color-support-contrast')}) !important;
        border-radius: 0 !important;
        border: 2px solid var(--movie-site_footer-subscribe-form-background-color-contrast, ${getVar('--movie-site_brand-color-support-contrast')}) !important;
        border-color: var(--movie-site_footer-subscribe-form-background-color-contrast, ${getVar('--movie-site_brand-color-support-contrast')}) !important;
      }

      .${BODY_CLASS_NAME} #footer-subscribe-form .subscribe-form  input::placeholder {
        color: var(--movie-site_footer-subscribe-form-background-color-contrast, ${getVar('--movie-site_brand-color-support-contrast')}) !important;
        opacity: 0.5 !important;
      }


      .${BODY_CLASS_NAME} #footer-subscribe-form button {
        background-color: ${getVar('--movie-site_brand-color')} !important;
        color: ${getVar('--movie-site_brand-color-contrast')} !important;
        border-radius: 0 !important;
        border-color: ${getVar('--movie-site_brand-color')} !important;
      }

      .${BODY_CLASS_NAME} #footer-subscribe-form button :is(span, :hover span, :focus span) {
        color: inherit !important;
      }

      .${BODY_CLASS_NAME} #footer-subscribe-form button:is(:hover, :focus) {
        background-color: ${getVar('--movie-site_brand-color-hover')} !important;
        color: ${getVar('--movie-site_brand-color-contrast')} !important;
        border-color: ${getVar('--movie-site_brand-color-hover')} !important;
      }

      .${BODY_CLASS_NAME} #footer {
        justify-content: var(--footer-justify-content, space-around);
      }

      .${BODY_CLASS_NAME} .footer-logo {
        overflow: hidden;      
      }
     
      .${BODY_CLASS_NAME} footer-component:not(.disable-drop-shadow) .footer-logo img {
        display: block;
        filter: drop-shadow(0px 1000px 0 ${getVar('--movie-site_background-color-contrast')});
        transform: translateY(-1000px);
      }

       .${BODY_CLASS_NAME} #footer .social-links a {
        background: ${getVar('--movie-site_background-color')} !important;
       }

       .${BODY_CLASS_NAME} #footer .social-links a fa-icon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        color: ${getVar('--movie-site_background-color-contrast')} !important;
       }

       .${BODY_CLASS_NAME} #footer .social-links a:is(:hover, :focus) {
        background: ${getVar('--movie-site_background-color-shaded')} !important;
       }

        .${BODY_CLASS_NAME} #footer .social-links a:is(:hover, :focus) fa-icon {
          color: ${getVar('--movie-site_background-color-contrast')} !important;
        }

        .${BODY_CLASS_NAME} .footer-wrapper .p-select {
          --inputBoxBackground: transparent !important;
          background-color: transparent !important;
        }

        .${BODY_CLASS_NAME} .footer-wrapper .p-select-label {
          color: ${getVar('--movie-site_background-color-contrast')} !important;
        }

        .${BODY_CLASS_NAME} .footer-wrapper .p-select-dropdown {
          background-color: ${getVar('--movie-site_brand-color')} !important;
          color: ${getVar('--movie-site_brand-color-contrast')} !important;
          border-radius: 0 !important;
        }

        .${BODY_CLASS_NAME} .footer-wrapper .p-select-option {
          background-color: #ffffff !important;
          color: black !important;
        }

        .${BODY_CLASS_NAME} .footer-wrapper .p-select-option:is(.p-focus, :hover, :focus) {
          background-color: #efefefff !important;
        }

        .${BODY_CLASS_NAME} .footer-wrapper .p-select-option span {
          color: inherit !important;
        }

        .${BODY_CLASS_NAME} #goToTop {
          background-color: ${getVar('--movie-site_brand-color')} !important;
          color: ${getVar('--movie-site_brand-color-contrast')} !important;
        }

        .${BODY_CLASS_NAME} ush-movie-reviews  {
          --swiper-navigation-color: ${getVar('--movie-site_background-color-contrast')};
        }

        .${BODY_CLASS_NAME} ush-movie-reviews :is(.subtitle, .review-item-source) {
          color: ${getVar('--movie-site_background-color-contrast')};
        }

        .${BODY_CLASS_NAME} ush-movie-rating .pi {
          --textColor: ${getVar('--movie-site_brand-color')};    
        }

        .${BODY_CLASS_NAME} .loading-wrapper {
          --loadingIndicatorColor: ${getVar('--movie-site_brand-color')};
          --priority-loading-indicator-color: ${getVar('--movie-site_brand-color')};
        }
    `;
